import React from "react";
import { ScrollAwareTooltip } from "@/components/shared/ScrollAwareTooltip";
import "./tooltip.scss";

interface TooltipPropsExtended {
  tooltipText: string | undefined;
  children: React.ReactElement<any>;
  /** When set, URL and `<link>` links call this instead of navigating (e.g. template download). */
  onLinkClick?: () => void;
  /** Optional URL for `<link>label</link>` when not using onLinkClick only. */
  linkHref?: string;
}

const LINK_TAG_REGEX = /<link>(.*?)<\/link>/gi;
const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
const URL_TEST_REGEX = /^(https?:\/\/[^\s]+|www\.[^\s]+)$/;

const normalizeUrl = (raw: string): string | null => {
  if (!raw) return null;

  const trimmed = raw.trim();

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  if (/^www\./i.test(trimmed)) {
    return `https://${trimmed}`;
  }

  return null;
};

const renderLink = (
  key: string | number,
  label: string,
  href: string | null,
  onLinkClick?: () => void
) => {
  if (onLinkClick) {
    return (
      <a
        key={key}
        href="#"
        className="text-blue-500 underline cursor-pointer"
        onClick={(e) => {
          e.preventDefault();
          onLinkClick();
        }}
      >
        {label}
      </a>
    );
  }

  if (href) {
    return (
      <a
        key={key}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 underline"
      >
        {label}
      </a>
    );
  }

  return <span key={key}>{label}</span>;
};

const renderPlainTextWithUrls = (
  text: string,
  keyPrefix: string,
  onLinkClick?: () => void
): React.ReactNode[] => {
  const parts = text.split(URL_REGEX);

  return parts.flatMap((part, index) => {
    if (!part.trim()) return [];

    if (URL_TEST_REGEX.test(part)) {
      const normalizedUrl = normalizeUrl(part);
      if (!normalizedUrl) {
        return [<span key={`${keyPrefix}-text-${index}`}>{part}</span>];
      }

      return [
        renderLink(
          `${keyPrefix}-url-${index}`,
          part,
          normalizedUrl,
          onLinkClick
        ),
      ];
    }

    return [<span key={`${keyPrefix}-text-${index}`}>{part}</span>];
  });
};

const convertTextToJSX = (
  text: string | undefined,
  onLinkClick?: () => void,
  linkHref?: string
) => {
  if (!text || typeof text !== "string" || text.trim() === "") {
    return null;
  }

  const normalizedLinkHref = linkHref ? normalizeUrl(linkHref) : null;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  LINK_TAG_REGEX.lastIndex = 0;
  while ((match = LINK_TAG_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(
        ...renderPlainTextWithUrls(
          text.slice(lastIndex, match.index),
          String(lastIndex),
          onLinkClick
        )
      );
    }

    const linkLabel = (match[1] || "link").trim() || "link";
    nodes.push(
      renderLink(
        `link-${match.index}`,
        linkLabel,
        normalizedLinkHref,
        onLinkClick
      )
    );

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(
      ...renderPlainTextWithUrls(text.slice(lastIndex), "tail", onLinkClick)
    );
  }

  return (
    <span style={{ whiteSpace: "pre-line", maxWidth: 300 }}>{nodes}</span>
  );
};

const CustomTooltip: React.FC<TooltipPropsExtended> = ({
  tooltipText,
  children,
  onLinkClick,
  linkHref,
}) => {
  const tooltipContent = convertTextToJSX(tooltipText, onLinkClick, linkHref);

  return (
    <ScrollAwareTooltip
      title={tooltipContent || ""}
      arrow
      placement="bottom-start"
      disableHoverListener={!tooltipContent}
      PopperProps={{
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [-5, 1],
            },
          },
        ],
      }}
      classes={{ tooltip: "custom-tooltip", arrow: "custom-tooltip-arrow" }}
    >
      {children}
    </ScrollAwareTooltip>
  );
};

export default CustomTooltip;
